import { useEffect } from 'react';
import { useLocation } from 'wouter';

export type WorkspaceMode = 'athlete' | 'coach';

// Remembers the last route per mode so switching returns the coach to where
// they left off on the other side.
export function useWorkspaceSwitch(mode: WorkspaceMode, enabled = true) {
  const [location, navigate] = useLocation();
  useEffect(() => {
    if (!enabled) return;
    try {
      sessionStorage.setItem(`standard-last-${mode}`, location);
      localStorage.setItem('standard-app-mode', mode);
    } catch { /* private browsing */ }
  }, [enabled, location, mode]);
  return (next: WorkspaceMode) => {
    if (next === mode) return;
    let target = next === 'athlete' ? '/coach/my/today' : '/coach';
    try {
      const saved = sessionStorage.getItem(`standard-last-${next}`);
      if (saved && (next === 'athlete' ? saved.startsWith('/coach/my/') || saved === '/coach/wins' : saved.startsWith('/coach') && !saved.startsWith('/coach/my/'))) target = saved;
    } catch { /* private browsing */ }
    navigate(target);
  };
}

/**
 * The one Training and Command switch (coach and admin accounts only). Phones
 * and iPad portrait show it first in the top bar, in the same place on both
 * sides; from 1100px the sidebar shows it instead and the top bar hides its
 * copy (standard-shell.css). data-workspace-switch marks every copy so the
 * suites can check that exactly one is visible at any size.
 */
export function WorkspaceSwitch({ mode, variant = 'bar' }: { mode: WorkspaceMode; variant?: 'bar' | 'sidebar' }) {
  const change = useWorkspaceSwitch(mode);
  if (variant === 'sidebar') {
    return <div className="standard-shell-mode" role="group" aria-label="Workspace" data-workspace-switch="sidebar">
      <button type="button" aria-pressed={mode === 'athlete'} onClick={() => change('athlete')}>Train</button>
      <button type="button" aria-pressed={mode === 'coach'} onClick={() => change('coach')}>Command</button>
    </div>;
  }
  return <div className="workspace-switch workspace-switch--embedded" role="group" aria-label="Workspace" data-workspace-switch="bar">
    <button type="button" aria-pressed={mode === 'athlete'} onClick={() => change('athlete')}>
      <span className="workspace-switch__full">My Training</span>
      <span className="workspace-switch__short">Training</span>
    </button>
    <button type="button" aria-pressed={mode === 'coach'} onClick={() => change('coach')}>
      <span className="workspace-switch__full">Command</span>
      <span className="workspace-switch__short">Command</span>
    </button>
  </div>;
}
